<script>
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import { fade } from 'svelte/transition';
  import Button from '@components/Button.svelte';
  import Input from '@components/Input.svelte';
  import Socials from '@utils/data/socials-data';
  import userAjax from '@ajax/user';
  import SocialLinkAjax from '@ajax/social_link';
  import queryString from 'query-string';
  import Track from '@utils/track';

  const socials = Socials.socialsData;
  const dispatch = createEventDispatcher();

  export let createdUser;
  export let prefilledInstagramHandle = null;
  export let prefilledTiktokHandle = null;
  export let isActive = false;
  export let mobile = false;

  let sf = typeof window !== 'undefined' && queryString.parse(window.location.search).sf;
  prefilledInstagramHandle = prefilledInstagramHandle || sf;

  let instagramHandle = prefilledInstagramHandle;
  let tiktokHandle = prefilledTiktokHandle;
  let instagramErrorMessage = null;
  let instagramInvalid = false;
  let tiktokErrorMessage = null;
  let tiktokInvalid = false;
  let submitButtonLoading = false;
  let showTiktokInput = false;
  let showSkipButton = false;
  let componentMounted = false;
  let isInstagramValid = false;

  let validChecks = {
    tiktok: {
      icon: null,
      loading: null,
      username: null,
    },
    instagram: {
      icon: null,
      loading: null,
      username: null,
    },
  };

  $: validInformation =
    (validChecks.instagram.icon || validChecks.tiktok.icon) && !instagramErrorMessage && !tiktokErrorMessage;
  $: showValidateInstagram =
    instagramHandle && instagramHandle.length > 2 && !validChecks.instagram.loading && !validChecks.instagram.icon;
  $: showValidateTiktok =
    tiktokHandle && tiktokHandle.length > 2 && !validChecks.tiktok.loading && !validChecks.tiktok.icon;
  $: showTiktokInput = true; // Always show TikTok input from the start

  $: if (isActive && componentMounted) {
    setTimeout(() => {
      showSkipButton = true;
    }, 5000);
  }

  let stripSlashes = function (string) {
    return string.replace(/^\/+|\/+$/g, '');
  };

  let instagramHandleToUrl = function (instagramHandle) {
    let handle = stripSlashes(instagramHandle.toLowerCase()).trim();

    if (handle.includes('instagram.com')) {
      return handle;
    }

    if (handle.startsWith('@')) {
      handle = handle.slice(1);
    }

    return `https://www.instagram.com/${handle}`;
  };

  let tiktokHandleToUrl = function (tiktokHandle) {
    let handle = stripSlashes(tiktokHandle.toLowerCase()).trim();

    if (handle.includes('tiktok.com')) {
      return handle;
    }

    if (!handle.startsWith('@')) {
      handle = `@${handle}`;
    }

    return `https://www.tiktok.com/${handle}`;
  };

  const saveAvailableInformationAndFinish = function () {
    if (validChecks.tiktok.loading || validChecks.instagram.loading) {
      submitButtonLoading = true;
    } else {
      saveAvailableInformation();
      Track.socialsEntered({
        ig: instagramHandle,
        tiktok: tiktokHandle,
      });
      dispatch('finished');
    }
  };

  let handleSkip = function () {
    Track.socialsSkip({
      ig: instagramHandle,
      tiktok: tiktokHandle,
    });
    dispatch('skip');
  };

  let setIconOrError = function (response, type, interval = null) {
    if (response.status == 'completed') {
      validChecks[type].loading = false;
      if (interval) {
        clearInterval(interval);
      }
      if (response.valid) {
        validChecks[type].icon = response.image;
        if (type == 'instagram') {
          isInstagramValid = true;
        }
        if (submitButtonLoading) {
          saveAvailableInformationAndFinish();
        } else {
          saveAvailableInformation();
        }
      } else {
        validChecks[type].icon = null;
        if (type == 'instagram') {
          instagramErrorMessage = 'No account found';
          isInstagramValid = false;
        } else if (type == 'tiktok') {
          tiktokErrorMessage = 'No account found';
        }
      }

      submitButtonLoading = false;
    }
  };

  let checkValid = async function (input, type) {
    if (!input) {
      validChecks[type] = { icon: null, loading: false, username: null };
      return;
    }

    input = input.toLowerCase();

    if (input == validChecks[type].username) {
      return;
    }

    if (type == 'instagram' && instagramInvalid) {
      return;
    }

    if (type == 'tiktok' && tiktokInvalid) {
      return;
    }

    validChecks[type] = { icon: null, loading: true, username: input };
    let interval;
    let maxInterval = 15000;

    let response = await SocialLinkAjax.queueCheckValid(input, type);
    if (response.status == 'completed') {
      setIconOrError(response, type);
    } else {
      interval = setInterval(async () => {
        response = await SocialLinkAjax.checkValid(input, type);
        setIconOrError(response, type, interval);
      }, 750);
    }

    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
      }
      validChecks[type].loading = false;
      if (submitButtonLoading) {
        saveAvailableInformationAndFinish();
      }
    }, maxInterval);
  };

  let saveAvailableInformation = function () {
    let socialLinks = [];
    if (instagramHandle && !instagramErrorMessage) {
      socialLinks.push({
        url: instagramHandleToUrl(instagramHandle),
        label: socials['instagram'].label,
        link_type: 'instagram',
        user_avatar_icon: validChecks['instagram'].icon,
      });
    }

    if (tiktokHandle && !tiktokErrorMessage) {
      socialLinks.push({
        url: tiktokHandleToUrl(tiktokHandle),
        label: socials['tiktok'].label,
        link_type: 'tiktok',
        user_avatar_icon: validChecks['tiktok'].icon,
      });
    }

    if (createdUser && socialLinks.length > 0) {
      userAjax.update(createdUser, { social_links: socialLinks });
    }
  };

  let validateInstagramInput = function (event) {
    let instagramRegex = /^[a-zA-Z0-9._]{1,30}$/;
    let instagramValue = event.detail.value.trim();
    if (instagramValue && !instagramRegex.test(instagramValue)) {
      instagramErrorMessage =
        "Instagram handles can only contain letters, numbers, periods, and underscores, and can't be more than 30 characters";
      instagramInvalid = true;
    } else {
      instagramErrorMessage = null;
      instagramInvalid = false;
    }
  };

  let validateTiktokInput = function (event) {
    let tiktokRegex = /^[a-zA-Z0-9._]{1,30}$/;
    let tiktokValue = event.detail.value.trim();

    if (tiktokValue && !tiktokRegex.test(tiktokValue)) {
      tiktokErrorMessage =
        "TikTok handles can only contain letters, numbers, periods, and underscores, and can't be more than 24 characters";
      tiktokInvalid = true;
    } else {
      tiktokErrorMessage = null;
      tiktokInvalid = false;
    }
  };

  onMount(async () => {
    await tick();
    componentMounted = true;

    if (prefilledInstagramHandle && prefilledInstagramHandle.length > 0) {
      checkValid(prefilledInstagramHandle, 'instagram');
    }
  });
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';

  .social-step {
    display: flex;
    flex-direction: column;
    padding: 20px;
    min-height: calc(100dvh - 114px);

    .content-container {
      flex: 1;
      max-width: 440px;
      width: 100%;
      margin: 0 auto;
    }

    .button-container {
      max-width: 440px;
      width: 100%;
      margin: 0 auto;
      padding-bottom: 44px;
    }

    .ui-title {
      font-size: 42px;
      line-height: 38px;
      text-align: center;
      font-weight: 800;
      text-transform: uppercase;
      margin-top: 64px;
      margin-bottom: 16px;
      color: #717171;
    }

    .ui-subtitle {
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      color: #7f7f7f;
      margin-bottom: 32px;
    }

    .ui-body {
      margin-left: auto;
      margin-right: auto;
      max-width: 400px;
      text-align: left;
    }

    .smiling-face {
      img {
        width: 64px;
        height: 64px;
      }
    }
  }

  .navigation-buttons {
    position: absolute;
    top: 46px;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 20px;
  }

  .back-btn,
  .skip-btn {
    font-weight: 500;
    font-size: 14px;
    color: #1a1a1a;
    cursor: pointer;
    border: none;
    padding: 0 !important;
    background: none;

    &:hover {
      color: $neutral-dark;
    }
  }

  .validate-btn {
    background: $purple-80;
    color: white;
    padding: 6px 12px;
    margin-right: -10px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: background 0.2s ease-in-out;

    &:hover {
      background: $purple-70;
    }
  }

  .mobile {
    min-height: calc(100dvh - 60px);

    .navigation-buttons {
      top: 18px;
    }
  }

  .tiktok-input-container {
    margin-bottom: 12px;
  }
</style>

<div class="social-step" class:mobile={mobile}>
  {#if componentMounted}
    <div class="navigation-buttons">
      <button class="back-btn" on:click={() => dispatch('back')}>Back</button>
      {#if showSkipButton}
        <button
          class="skip-btn test-social-skip-btn"
          class:hidden-class={isInstagramValid}
          transition:fade
          on:click={handleSkip}>
          Skip
        </button>
      {/if}
    </div>
  {/if}

  <!-- Invisible button for testing purposes -->
  <button
    class="test-social-skip-btn"
    style="position: absolute; top: 0; right: 0; width: 1px; height: 1px; opacity: 0; z-index: 9999;"
    on:click={handleSkip}>
    Skip
  </button>
  <div class="content-container">
    <div class="ui-title national-2-narrow">Socials</div>
    <div class="ui-subtitle">Add your social handles to build your profile</div>

    <div class="ui-body">
      <form autocomplete="off" method="post" action="#" on:submit|preventDefault>
        {#if showTiktokInput}
          <div class="tiktok-input-container" transition:fade>
            <Input
              bind:value={tiktokHandle}
              leftIconPath={'/images/profile/socials/tiktok.svg'}
              on:change={(event) => {
                validChecks.tiktok = { icon: null, loading: null, username: null };
                validateTiktokInput(event);
              }}
              error={tiktokErrorMessage}
              placeholder={'username'}
              loading={false}
              withRightIconSlot={true}
              on:blur={() => checkValid(tiktokHandle, 'tiktok')}>
              <svelte:fragment slot="right-side-icon">
                {#if validChecks.tiktok.loading}
                  <div class="input-icon-right">
                    <i class="fa fa-spin fa-spinner" />
                  </div>
                {:else if validChecks.tiktok.icon}
                  <div>
                    <img
                      alt="tiktok-icon"
                      height={32}
                      width={32}
                      style="display: inline-block; object-fit: cover; border-radius: 9999px;"
                      src={validChecks.tiktok.icon} />
                    <img
                      alt="checkbox-icon"
                      style="position: absolute; right: -5px; bottom: -5px"
                      width={16}
                      src="/images/rec_request/checkbox.svg" />
                  </div>
                {:else if showValidateTiktok}
                  <button class="validate-btn" on:click={() => checkValid(tiktokHandle, 'tiktok')}> Validate </button>
                {/if}
              </svelte:fragment>
            </Input>
          </div>
        {/if}

        <Input
          bind:value={instagramHandle}
          leftIconPath={'/images/profile/socials/instagram.svg'}
          on:change={(event) => {
            validChecks.instagram = { icon: null, loading: null, username: null };
            validateInstagramInput(event);
          }}
          error={instagramErrorMessage}
          errorClass={'instagram-input-error-msg'}
          placeholder={'username'}
          loading={false}
          withRightIconSlot={true}
          on:blur={() => checkValid(instagramHandle, 'instagram')}>
          <svelte:fragment slot="right-side-icon">
            {#if validChecks.instagram.loading}
              <div class="input-icon-right">
                <i class="fa fa-spin fa-spinner" />
              </div>
            {:else if validChecks.instagram.icon}
              <div>
                <img
                  alt="instagram-icon"
                  height={32}
                  width={32}
                  style="display: inline-block; object-fit: cover; border-radius: 9999px;"
                  src={validChecks.instagram.icon} />
                <img
                  alt="checkbox-icon"
                  style="position: absolute; right: -5px; bottom: -5px"
                  width={16}
                  src="/images/rec_request/checkbox.svg" />
              </div>
            {:else if showValidateInstagram}
              <button class="validate-btn" on:click={() => checkValid(instagramHandle, 'instagram')}> Validate </button>
            {/if}
          </svelte:fragment>
        </Input>
      </form>
    </div>
  </div>
  <div class="button-container">
    <Button
      text="Continue"
      loading={submitButtonLoading}
      disabled={!validInformation}
      on:click={saveAvailableInformationAndFinish} />
  </div>
</div>
